import axios from 'app/client';
import { AxiosError } from 'axios';
import { getProfile, getSelectedLicenseGroupId, getTenantManagerGroupsById } from 'app/store/reducers';
import {
	errorCodes,
	EXPIRED_GRACE_PERIOD,
	getFormsUrl,
	getR2mUrl,
	getJntUrl,
	getLogApiUrl,
	getWfxUrl,
	getNodeTestingAPI,
	// getFileserviceUrl,
	groupByID,
	responseError,
	SAMPLE_FORMS_GROUP,
	SAMPLE_FORMS_GROUP_ID,
	SAMPLE_WORKFLOWS_GROUP,
	SAMPLE_WORKFLOWS_GROUP_ID,
	type PrintPreferences,
	arrayify,
	getFormFieldDisplayValue,
	getFileserviceUrl,
} from 'app/utils/helpers';

import _ from '@lodash';
import { type AppThunk } from 'app/store';
import {
	type AdminInfo,
	type Device,
	type DeviceGroup,
	type FormGroup,
	type FormOverview,
	type LicenseGroupData,
	type Log,
	type PendingUser,
	type Profile,
	type PublicId,
	type TenantGroup,
	type User,
	type UserGroup,
	type Workflow,
	type WorkflowGroup,
	type Role,
	type InputNode,
	type DirectPrintAgent,
	type SampleWorkflow,
	type Notification,
	type NotificationGroup,
	type App,
	type LicenseGroupResource,
	type AppGroup,
	type Print,
	type PrintGroup,
	type JobName,
	type JobNameGroup,
	type MFA,
	type BSN,
	type BsnDevices,
	type BsnPolicyGroups,
	type BsnAvailableDevices,
	type BsnDeviceOptions,
	type authProvider,
	type MfpAuthSetting,
	type ScheduledReport,
	type ArchivedJob,
	type ArchivedFormFullData,
	type MyFile,
	type TrashedStorageNode,
} from 'app/store/types';
import { getTimeZones } from '@vvo/tzdb';
import { UNGROUPED_APP_GROUP_ID, UNGROUPED_BSN_GROUP_ID, UNGROUPED_FORMS_GROUP_ID } from 'app/utils/default-groups';
import { getPlanDomain } from 'app/utils/tenant-plan';
import {
	adminInfoSchema,
	appGroupsSchema,
	appsSchema,
	BSNSchema,
	deviceGroupsSchema,
	devicesSchema,
	entitlementsSchema,
	formGroupsSchema,
	formsSchema,
	inputNodesSchema,
	jobNameGroupsSchema,
	jobNamesSchema,
	licenseSchema,
	pendingUsersSchema,
	rolesSchema,
	sampleWorkflowsSchema,
	userGroupsSchema,
	usersSchema,
	workflowGroupsSchema,
	workflowsSchema,
	trashedStorageNodeSchema,
} from 'app/utils/schemas';
import * as appActions from './app.actions';
import * as profileActions from './profile.actions';

// HACK::allow switching license group plan for testing
const mockSelectedLicenseGroupPlan = localStorage.getItem('mockPlan') as PublicId | undefined;

// HACK::allow switching order type for testing
const mockSelectedLicenseGroupOrderType = localStorage.getItem('mockOrderType') as
	| LicenseGroupData['orderType']
	| undefined;

// HACK::allow switching to expired license for testing
const mockExpired = localStorage.getItem('mockExpired');

// HELPER: set isCoolOffPeriod based on the order type and the expiration date
const getCoolOffPeriod = (
	orderType: LicenseGroupData['orderType'],
	expirationDate: LicenseGroupData['expirationDate'],
): boolean => {
	return (
		!!expirationDate &&
		Date.now() >= expirationDate &&
		Date.now() < expirationDate + EXPIRED_GRACE_PERIOD[orderType]
	);
};

const getTimezoneOffset = (timezoneName: string) => {
	const timeZones = getTimeZones({ includeUtc: true });
	let userTimeZoneData = timeZones.find((tz) => tz.name === timezoneName);
	if (!userTimeZoneData) {
		userTimeZoneData = timeZones.find((tz) => tz.group.includes(timezoneName));
	}
	return userTimeZoneData ? userTimeZoneData.currentTimeOffsetInMinutes : -new Date().getTimezoneOffset();
};

// HACK::reshape data returned from backend
/* eslint-disable @typescript-eslint/no-explicit-any */
// DEV NOTE::do *not* copy this `eslint-disable` line anywhere else except massager functions - it is the only exception
const massageRawLicenseGroup = (rawLicenseGroup: any) => {
	const timezoneOffset = getTimezoneOffset(rawLicenseGroup.timezone);

	const licenseGroupData: LicenseGroupData = {
		id: rawLicenseGroup.id,
		slug: rawLicenseGroup.slug,
		domain: `https://${rawLicenseGroup.slug}.tenant.${getPlanDomain(rawLicenseGroup.plan)}`,
		name: rawLicenseGroup.name,
		quantity: rawLicenseGroup.quantity,
		capacity: rawLicenseGroup.capacity,
		userQuantity: rawLicenseGroup.userQuantity,
		userCapacity: rawLicenseGroup.userCapacity,
		r2mQuantity: rawLicenseGroup.r2mQuantity,
		r2mCapacity: rawLicenseGroup.r2mCapacity,
		materialNumber: rawLicenseGroup.materialNumber,
		// HACK::publicId can come in upper- or lower-case
		catalogPublicId: mockSelectedLicenseGroupPlan ?? rawLicenseGroup.catalogPublicId?.toUpperCase(),
		orderType: mockSelectedLicenseGroupOrderType ?? rawLicenseGroup.orderType?.toUpperCase(),
		orderNumber: rawLicenseGroup.orderNumber,
		status: rawLicenseGroup.status,
		purchaseCode: rawLicenseGroup.purchaseCode,
		expirationDate: mockExpired ? +mockExpired : rawLicenseGroup.expirationDate,
		userExpirationDate: mockExpired ? +mockExpired : rawLicenseGroup.userExpirationDate,
		r2mExpirationDate: mockExpired ? +mockExpired : rawLicenseGroup.r2mExpirationDate,
		isCoolOffPeriod:
			getCoolOffPeriod(
				mockSelectedLicenseGroupOrderType ?? rawLicenseGroup.orderType?.toUpperCase(),
				mockExpired ? +mockExpired : rawLicenseGroup.expirationDate,
			) ?? false,
		language: rawLicenseGroup.language,
		region: rawLicenseGroup.region,
		timezone: rawLicenseGroup.timezone,
		timezoneOffset,
		managerEmails: rawLicenseGroup.managerEmails,
		parentId: rawLicenseGroup?.parent,
		adminData: rawLicenseGroup.adminData,
		dateCreated: rawLicenseGroup.dateAdded,
		authMethod: rawLicenseGroup.authMethod,
		licenseUsage: rawLicenseGroup.licenseUsage,
		suspension: rawLicenseGroup.suspensions ?? {},
		// preferences: rawLicenseGroup.preferences ?? {}, pretty sure these manager notification preferences were moved to manager groups
		plan: rawLicenseGroup.plan,
		directPrintEnabled: rawLicenseGroup.directPrintEnabled,
		r2mEnabled: rawLicenseGroup.r2mEnabled,
		msUnivPrintEnabled: rawLicenseGroup.msUnivPrintEnabled,
		bsnDashboardEnabled: rawLicenseGroup.bsnDashboardEnabled,
		mfpAuthEnabled: rawLicenseGroup.mfpAuthEnabled,
		autoProvisioningEnabled: rawLicenseGroup.autoProvisioningEnabled,
		autoProvisioningGroup: rawLicenseGroup.autoProvisioningGroup,
		storageEnabled: rawLicenseGroup.storageEnabled ?? false,
		storageExpiresAt: rawLicenseGroup.storageExpiresAt,
		jobArchiveDuration: rawLicenseGroup.jobArchiveDuration ?? 365 * 24 * 60 * 60,
		jobTrashDuration: rawLicenseGroup.jobTrashDuration ?? 30 * 24 * 60 * 60,
		mfpDashboardEnabled: rawLicenseGroup.mfpDashboardEnabled,
		filesEnabled: rawLicenseGroup.filesEnabled ?? false,
		fileRetentionDuration: rawLicenseGroup.fileRetentionDuration,
		fileTrashDuration: rawLicenseGroup.fileTrashDuration,
	};

	return licenseGroupData;
};

const massageRawTenantManagerGroup = (rawTenantGroup: any) => {
	const tenantGroup: TenantGroup = {
		id: rawTenantGroup.id,
		parentId: rawTenantGroup?.parentId,
		protected: rawTenantGroup?.protected,
		children: rawTenantGroup?.children,
		parentName: rawTenantGroup?.parentName,
		managerEmails: rawTenantGroup?.managerEmails?.map((manager: any) => ({
			firstName: manager?.firstName,
			lastName: manager?.lastName,
			email: manager?.email,
			status: manager?.status,
			id: manager?.id,
		})),
		name: rawTenantGroup.name,
		managerEvents: rawTenantGroup?.preferences,
		isGroup: true,
	};

	return tenantGroup;
};

const massageRawAdminInfo = (rawAdminInfo: any) => {
	const adminInfo: AdminInfo = {
		primary: rawAdminInfo.primary,
		secondary: rawAdminInfo.secondary,
	};

	return adminInfo;
};

const massageRawUser = (rawUser: any) => {
	const user: User = {
		id: rawUser.id,
		email: rawUser.email,
		// HACK-ish::fall back to `username` and empty string for firstName and lastName - added for cognito users
		firstName: rawUser.firstName || rawUser.username || rawUser.email.split('@')[0] || '',
		lastName: rawUser.lastName || '',
		dateAdded: rawUser.dateAdded,
		expireDate: rawUser.expireDate ? rawUser.expireDate * 1000 : undefined,
		hidden: rawUser.hidden ?? false,
		userGroupId: rawUser.groupList,
		blocked: rawUser.status === 'BLOCKED',
		// DEV NOTE::in theory this should always be false (because `massageRawPendingUser` handled pending users)
		pending: rawUser.status === 'PENDING',
		username: rawUser.userName,
		roles: rawUser.roles,
		licensed: rawUser.licensed,
		nonMfpLicenseCapacity: rawUser.nonMFPLicenseCapacity,
		nonMfpLicenseQuantity: rawUser.nonMFPLicenseQuantity,
		badgeNumber: rawUser.badgeNumber ? rawUser.badgeNumber : false,
		userPin: rawUser.pinNumber ? rawUser.pinNumber : false,
	};

	return user;
};

const massageRawPendingUser = (rawPendingUser: any) => {
	const pendingUser: PendingUser = {
		id: rawPendingUser.id,
		email: rawPendingUser.email,
		token: rawPendingUser.token,
		dateAdded: rawPendingUser.dateAdded,
		expireDate: rawPendingUser.expireDate ? rawPendingUser.expireDate * 1000 : undefined,
		hidden: rawPendingUser.hidden ?? false,
		userGroupId: rawPendingUser.groupList ? rawPendingUser.groupList : [],
		blocked: false,
		// add `pending` prop for distinguishing if this list gets mixed with regular users
		pending: true,
		roles: rawPendingUser.roles,
		licensed: rawPendingUser.licensed,
		nonMfpLicenseCapacity: rawPendingUser.nonMFPLicenseCapacity,
		nonMfpLicenseQuantity: rawPendingUser.nonMFPLicenseQuantity,
		badgeNumber: rawPendingUser.badgeNumber ? rawPendingUser.badgeNumber : '',
		userPin: rawPendingUser.pinNumber ? rawPendingUser.pinNumber : '',
	};

	return pendingUser;
};

const massageRawUserGroup = (rawUserGroup: any) => {
	const userGroup: UserGroup = {
		id: rawUserGroup.id,
		name: rawUserGroup.name,
		dateUpdated: rawUserGroup.updatedAt,
		groupOrder: rawUserGroup.groupOrder,
		collapse: rawUserGroup.collapse,
		roles: rawUserGroup?.roles ?? [],
		defaultUser: rawUserGroup.defaultUser,
	};

	return userGroup;
};

const massageRawDevice = (rawDevice: any) => {
	if (rawDevice.id === 'ADXV013000009') {
		console.log('DEBUG: massageRawDevice - Raw device data:', JSON.stringify(rawDevice, null, 2));
	}
	const device: Device = {
		serial: rawDevice.id,
		friendlyName: rawDevice.name,
		name: rawDevice.model ? `bizhub ${rawDevice.model}` : '',
		model: rawDevice.model,
		location: rawDevice.location,
		STCApp: rawDevice.STCApp ?? false,
		bsnApp: rawDevice.BSNApp ?? false,
		stratusApp: rawDevice.stratusApp ?? false,
		authRegistered: rawDevice.authRegistered,
		mfpAuthStatus: rawDevice.mfpAuthStatus,
		r2mRegistered: rawDevice.r2mRegistered,
		bsnRegistered: rawDevice.bsnRegistered,
		ipAddress: rawDevice.ip,
		serialNumber: rawDevice.serial,
		description: rawDevice.description,
		licensed: rawDevice.licensed,
		variant: rawDevice.variant ?? undefined,
		directPrintAgentId: rawDevice.directPrintAgentId,
		printProtocol: rawDevice.printProtocol,
		lprPort: rawDevice.lprPort,
		lprQueueName: rawDevice.lprQueueName,
		lprHostName: rawDevice.lprHostName,
		lprUserName: rawDevice.lprUserName,
		rawPort: rawDevice.rawPort,
		supportsPjlHeaders: rawDevice.supportsPjlHeaders,
		deviceGroupId: rawDevice.groupList.length === 1 && rawDevice.groupList[0] === '' ? [] : rawDevice.groupList,
		deviceStatus: rawDevice.status ? rawDevice.status.toLowerCase() : 'complete',
		universalPrintRegistrationStatus: rawDevice.universalPrintRegistrationStatus ?? undefined,
	};
	return device;
};

const massageRawDeviceGroup = (rawDeviceGroup: any) => {
	const deviceGroup: DeviceGroup = {
		id: rawDeviceGroup.id,
		name: rawDeviceGroup.name,
		dateUpdated: rawDeviceGroup.dateUpdated,
	};

	return deviceGroup;
};

const massageRawInputNode = (rawInputNode: any) => {
	const inputNode: InputNode = {
		id: rawInputNode.id,
		name: rawInputNode.name,
		licensed: rawInputNode.licensed,
	};

	return inputNode;
};

const massageRawDirectPrintAgent = (rawAgent: any) => {
	const agent: DirectPrintAgent = {
		id: rawAgent.attributes.AgentId,
		name: rawAgent.attributes.AgentName,
	};

	return agent;
};

const massageRawRole = (rawRole: any) => {
	const role: Role = {
		id: rawRole.id,
		name: rawRole.name,
		permissions: rawRole.permissions,
		systemRole: rawRole?.systemRole,
	};

	return role;
};

const massageRawForm = (rawForms: any[]): { [id: string]: FormOverview } => {
	const forms: { [id: string]: FormOverview } = {};

	rawForms.forEach((rawForm: any, i) => {
		const form: FormOverview = {
			id: rawForm.id,
			title: rawForm.title || `Form-${i + 1}`,
			creator: rawForm.creator,
			editor: rawForm.editor,
			created: rawForm.created,
			updated: rawForm.updated,
			formStatus: rawForm.formStatus,
			valid: rawForm.valid,
			metadataKeys: rawForm.metadataKeys,
			formGroup: { id: rawForm.formGroup.id },
		};

		forms[rawForm.id] = form;
	});

	return forms;
};

const massageRawFormGroup = (rawFormGroups: any): { [id: string]: FormGroup } => {
	const formGroups: { [id: string]: FormGroup } = {};

	rawFormGroups.forEach((rawFormGroup: any) => {
		const formGroup: FormGroup = {
			id: rawFormGroup.id,
			title: rawFormGroup.title,
			groupOrder: rawFormGroup.sortOrder,
		};

		formGroups[rawFormGroup.id] = formGroup;
	});

	formGroups[UNGROUPED_FORMS_GROUP_ID] = {
		id: UNGROUPED_FORMS_GROUP_ID,
		title: UNGROUPED_FORMS_GROUP_ID,
	};

	return formGroups;
};

const getPageCount = (rawWorkflow: any) => {
	let pageCount = 0;

	if (rawWorkflow.options?.PreviewPageCount) {
		pageCount = +rawWorkflow.options.PreviewPageCount;

		if (Number.isNaN(pageCount)) {
			pageCount = 1;
		}
	}

	return pageCount;
};

export const massageRawWorkflow = (
	rawWorkflow: any,
	profileId?: Profile['id'],
	awsRegion: Profile['awsRegion'] = 'us-east-1',
) => {
	const pageCount = getPageCount(rawWorkflow);
	const wfxApiUrl = getWfxUrl(awsRegion);
	const workflow: Workflow = {
		id: rawWorkflow.id,
		name: rawWorkflow.name,
		status: rawWorkflow.status,
		previews: _.range(0, pageCount).map(
			(count) =>
				`${wfxApiUrl}/api/wfx/${rawWorkflow.id}/preview/${count + 1}.png?${Date.parse(rawWorkflow.modified)}`,
		),
		// have a set shape - and always set yourself as the owner if no one is set yet
		acl: {
			users: rawWorkflow.acl?.users ?? [{ name: profileId, role: 'owner' }],
			groups: rawWorkflow.acl?.groups ?? [],
			devices: rawWorkflow.acl?.devices ?? [],
			deviceGroups: rawWorkflow.acl?.deviceGroups ?? [],
		},
		workflowGroupId: rawWorkflow.options?.Group || rawWorkflow.options?.group || '',
		dateUpdated: Date.parse(rawWorkflow.modified),
	};

	return workflow;
};

export const massageRawSampleWorkflow = (rawSampleWorkflow: any) => {
	const sampleWorkflow: SampleWorkflow = {
		id: rawSampleWorkflow.id,
		name: rawSampleWorkflow.name,
		status: rawSampleWorkflow.status,
		description: rawSampleWorkflow.description,
		dateUpdated: Date.parse(rawSampleWorkflow.modified),
		workflowGroupId: SAMPLE_WORKFLOWS_GROUP_ID,
	};

	return sampleWorkflow;
};

const massageRawWorkflowGroup = (rawWorkflowGroup: any, profileId?: Profile['id']) => {
	const workflowGroup: WorkflowGroup = {
		id: rawWorkflowGroup.id,
		name: rawWorkflowGroup.name,
		dateUpdated: new Date(rawWorkflowGroup.modified).getTime(),
		dateCreated: rawWorkflowGroup.created ? new Date(rawWorkflowGroup.created).getTime() : undefined,
		groupOrder: rawWorkflowGroup.groupOrder,
		collapse: rawWorkflowGroup.collapse,
		acl: {
			users: rawWorkflowGroup.acl?.users ?? [{ name: profileId, role: 'owner' }],
			groups: rawWorkflowGroup.acl?.groups ?? [],
			devices: rawWorkflowGroup.acl?.devices ?? [],
			deviceGroups: rawWorkflowGroup.acl?.deviceGroups ?? [],
		},
	};

	return workflowGroup;
};

const massageRawLog = (rawLog: any) => {
	const log: Log = {
		id: rawLog.SK,
		type: rawLog.Type,
		dateCreated: rawLog.DateAdded,
		severity: rawLog.severity,
		messageKey: rawLog.MessageTranslated || rawLog.MsgKey,
		errorCode: errorCodes[rawLog.MsgKey as keyof typeof errorCodes] ?? errorCodes.generalError,
		event: rawLog.Action,
		info: rawLog.Info ?? {},
		logLevel: rawLog.Action === 'error' ? 'error' : rawLog.LogLevel || 'info',
	};

	return log;
};

const massageRawEntitlements = (rawEntitlement: any) => {
	const { permissions } = rawEntitlement;
	const { features } = rawEntitlement;

	return {
		permissions: permissions ?? {},
		features: features ?? {},
	};
};
export const massageRawApp = (rawApp: any, profileId?: Profile['id']) => {
	const app: App = {
		id: rawApp.id,
		type: rawApp.type,
		name: rawApp.name,
		description: rawApp.description,
		url: rawApp.url,
		workflowId: rawApp.workflowId,
		nodeId: rawApp.nodeId,
		licensed: rawApp.licensed,
		appGroupId: rawApp.groupList?.length ? rawApp.groupList : [UNGROUPED_APP_GROUP_ID],
		// have a set shape - and always set yourself as the owner if no one is set yet
		acl: {
			users: rawApp.acl?.users ?? [{ name: profileId, role: 'owner' }],
			groups: rawApp.acl?.groups ?? [],
			// DEV NOTE::ATM `devices` and `deviceGroups` are not used for apps
			devices: rawApp.acl?.devices ?? [],
			deviceGroups: rawApp.acl?.deviceGroups ?? [],
		},
		aclRole: rawApp.aclRole,
	};

	return app;
};

const massageRawAppGroup = (rawAppGroup: any) => {
	const appGroup: AppGroup = {
		id: rawAppGroup.id,
		name: rawAppGroup.name,
	};

	return appGroup;
};

export const massageRawPrintPreferences = (rawPrintPreferences?: any) =>
	({
		guestDuration: 7 * 24 * 60 * 60,
		printJobExpirationDuration: 7 * 24 * 60 * 60,
		guestQueueExpirationDuration: 7 * 24 * 60 * 60,
		copies: 1,
		headers: [],
		...(rawPrintPreferences ?? {}),
	}) as PrintPreferences;

export const massageRawPrint = (rawPrint: any, profileId?: Profile['id']) => {
	const print: Print = {
		id: rawPrint.id,
		name: rawPrint.name,
		pageCount: rawPrint.pageCount,
		fileId: rawPrint.fileId,
		fileSize: rawPrint.fileSize,
		dateCreated: rawPrint.dateCreated,
		dateUpdated: rawPrint.dateUpdated,
		expireDate: rawPrint.expireDate ? rawPrint.expireDate * 1000 : undefined,
		printGroupId: rawPrint.printQueueId,
		acl: {
			users: rawPrint.acl?.users ?? [{ name: profileId, role: 'owner' }],
			groups: rawPrint.acl?.groups ?? [],
		},
		aclRole: rawPrint.acl?.users.find(({ name }: { name: any }) => name === profileId)?.role ?? 'view',
		ownerId: rawPrint.acl?.users.find(({ role }: { role: any }) => role === 'owner')?.name ?? '',
		metadata: rawPrint.metadata,
		senderEmail: rawPrint.senderEmail,
	};

	return print;
};

const massageRawPrintGroup = (rawPrintGroup: any, profileId?: Profile['id']) => {
	const printGroup: PrintGroup = {
		id: rawPrintGroup.id,
		slug: rawPrintGroup.slug,
		name: rawPrintGroup.name,
		type: rawPrintGroup.type,
		deviceId: rawPrintGroup.deviceId,
		rawActive: rawPrintGroup.rawActive,
		active: rawPrintGroup.active,
		secure: rawPrintGroup.secure,
		usedCount: rawPrintGroup.usedCount,
		dateCreated: rawPrintGroup.dateCreated ?? 0,
		dateUpdated: rawPrintGroup.dateUpdated ?? 0,
		expireDate: rawPrintGroup.expireDate ? rawPrintGroup.expireDate * 1000 : undefined,
		preferences: massageRawPrintPreferences(rawPrintGroup.preferences),
		userPreferences: massageRawPrintPreferences(rawPrintGroup.userPreferences),
		acl: {
			users: rawPrintGroup.acl?.users ?? [{ name: profileId, role: 'owner' }],
			groups: rawPrintGroup.acl?.groups ?? [],
		},
		aclRole: rawPrintGroup.acl?.users.find(({ name }: { name: any }) => name === profileId)?.role ?? 'view',
	};

	return printGroup;
};

const massageRawScheduledReport = (rawScheduledReport: any) => {
	const scheduledReport: ScheduledReport = {
		id: rawScheduledReport.id,
		name: rawScheduledReport.name,
		reportType: rawScheduledReport.reportType,
		format: rawScheduledReport.format,
		frequency: rawScheduledReport.frequency,
		dateUpdated: rawScheduledReport.dateUpdated,
		omitColumns: rawScheduledReport.omitColumns,
		excludeRows: rawScheduledReport.excludeRows,
		acl: rawScheduledReport.acl,
	};

	return scheduledReport;
};

export const massageRawNotification = (rawNotification: any) => {
	const notification: Notification = {
		id: rawNotification.id,
		acknowledgedId: `${rawNotification.id}-${rawNotification.dateUpdated}`,
		title: rawNotification.title,
		message: rawNotification.message,
		userGroupIds: rawNotification.userGroupIds,
		startDate: rawNotification.startDate,
		endDate: rawNotification.endDate,
		createdBy: rawNotification.createdBy,
		updatedBy: rawNotification.updatedBy,
		dateCreated: rawNotification.dateCreated,
		dateUpdated: rawNotification.dateUpdated,
		color: rawNotification.color,
		icon: rawNotification.icon,
	};

	return notification;
};

const massageRawNotificationGroup = (rawNotificationGroup: any) => {
	const notificationGroup: NotificationGroup = {
		id: rawNotificationGroup.id,
		name: rawNotificationGroup.name,
	};

	return notificationGroup;
};

const massageRawJobName = (rawJobName: any) => {
	const jobName: JobName = {
		id: rawJobName.id,
		name: rawJobName.name,
		description: rawJobName.description,
		components: rawJobName.components,
		modified: rawJobName.modified,
		created: rawJobName.created,
		editor: rawJobName.editor,
		creator: rawJobName.creator,
		groupList: rawJobName.groupList,
		nodes: rawJobName.nodes,
	};

	return jobName;
};

const massageRawJobNameGroup = (rawJobNameGroup: any) => {
	const jobNameGroup: JobNameGroup = {
		id: rawJobNameGroup.id,
		title: rawJobNameGroup.title,
	};

	return jobNameGroup;
};

/**
 * Form-related fields in metadata.data that should always be arrays.
 * Older archives may have these as single objects instead of arrays.
 */
const FORM_ARRAY_FIELDS = ['form', 'formsubmission', 'formsavedstate', 'formlayout'];

/**
 * Normalize form-related fields in metadata to always be arrays.
 * This ensures consistent data structure regardless of whether
 * the workflow had single or multiple form submissions.
 */
const normalizeMetadataFormFields = (metadata: any): any => {
	if (!metadata?.data) {
		return metadata;
	}

	const normalizedData = { ...metadata.data };

	for (const field of FORM_ARRAY_FIELDS) {
		if (normalizedData[field] !== undefined && normalizedData[field] !== null) {
			if (!Array.isArray(normalizedData[field])) {
				normalizedData[field] = [normalizedData[field]];
			}
		}
	}

	return {
		...metadata,
		data: normalizedData,
	};
};

const getFileUuidFromUrl = ({ url }: ArchivedJob['files'][number]) => {
	// NOTE::this can include an extension...but that appears to match what we get back from S3
	const uuid = url?.split('?')[0].split('/').pop() ?? undefined;
	return uuid;
};

const massageRawArchivedJob = (rawArchivedJob: any): ArchivedJob => {
	const jobId = rawArchivedJob.SK.split(':').pop();
	const rawMetadata = rawArchivedJob.RetentionSettings?.metadata ? (rawArchivedJob.Metadata ?? {}) : {};
	const normalizedMetadata = normalizeMetadataFormFields(rawMetadata);

	const formDefinitionsWithoutErrors = (rawArchivedJob.FormDefinitions ?? []).filter((formDefinition: any) => {
		const isError = !!formDefinition.code;
		if (isError) {
			console.error(`Form definition ${formDefinition.id} has an error: ${formDefinition.code}`);
		}
		return !isError;
	});

	// HACK::some meta files (like a screenshot of the form) are not passing through correctly so...we're using `uuid` and `ext` to find them and just pull them out to...
	// their own property (which also gets massaged with a uuid in the end so they at least have one). This is...
	// ...super fragile - these metafiles should have explicit properties, instead we're just assuming the...
	// ...lack of properties (which should be the case and that too may change) is proof of them being, e.g., the form screenshots
	// (the `ext` check was added because "Convert to PDF" has no `uuid` (bug?) but does at least have an `ext` in the metadata)
	const isMetaFile = (file: any) => !file.metadata?.uuid && !file.metadata?.ext;

	const rawFiles = rawArchivedJob.RetentionSettings?.attachments ? (rawArchivedJob.Files ?? []) : [];

	const massageRawFile = (rawFile: any, index: number) => ({
		...rawFile,
		metadata: {
			...rawFile.metadata,
			uuid: rawFile.metadata?.uuid || getFileUuidFromUrl(rawFile) || `file-${jobId}--${index}`,
		},
	});

	const archivedJob: ArchivedJob = {
		id: jobId,
		name: rawArchivedJob.Job?.Name || 'Unknown',
		workflow: { name: rawArchivedJob.Job?.WorkflowName || 'Unknown' },
		createdAt:
			(rawArchivedJob.CreatedAt ? rawArchivedJob.CreatedAt * 1000 : undefined) ??
			rawArchivedJob.Job?.UpdatedAt ??
			0,
		updatedAt:
			(rawArchivedJob.UpdatedAt ? rawArchivedJob.UpdatedAt * 1000 : undefined) ??
			rawArchivedJob.Job?.UpdatedAt ??
			0,
		history: rawArchivedJob.History ?? [],
		notes: rawArchivedJob.Notes ?? [],
		files: rawFiles.filter((file: any) => !isMetaFile(file)).map(massageRawFile),
		metaFiles: rawFiles.filter((file: any) => isMetaFile(file)).map(massageRawFile),
		job: rawArchivedJob.Job ?? { Drop: { username: '', password: '', id: '' } },
		metadata: normalizedMetadata,
		archived: rawArchivedJob.Archived,
		trashedArchive: rawArchivedJob.TrashedArchive,
		expiresAtDate: rawArchivedJob.ExpiresAt ? rawArchivedJob.ExpiresAt * 1000 : 0,
		maxExpiresAtDate: rawArchivedJob.MaxExpiresAt ? rawArchivedJob.MaxExpiresAt * 1000 : 0,
		retentionSettings: rawArchivedJob.RetentionSettings ?? { metadata: false, attachments: false },
		acl: {
			users: rawArchivedJob.Acl?.users ?? [],
			groups: rawArchivedJob.Acl?.groups ?? [],
		},
		formScreenshots: arrayify(rawArchivedJob.Forms) ?? [],
		formDefinitions: formDefinitionsWithoutErrors,
		formData: rawArchivedJob.RetentionSettings?.metadata
			? (formDefinitionsWithoutErrors as ArchivedFormFullData[])
					.map((form) => {
						const formMetadata: { [key: string]: string } =
							normalizedMetadata.data?.form?.find(
								(f: { [key: string]: string }) => f['.id'] === form.id,
							) ?? {};
						if (!formMetadata) {
							return null!;
						}
						return {
							id: form.id ?? '',
							title: form.title ?? '',
							// name: form.name, // DEV NOTE::it's crazy this isn't included...but it's not
							fields: (form.formDefinition?.fields ?? [])
								.map((field) => {
									const formMetadataValue: string | undefined = formMetadata[field.config.variable];
									const fieldValue = getFormFieldDisplayValue(field, formMetadataValue);
									// add `?? formMetadata[`${field.config.variable}!exclude`]` if we ever want to include the "Exclude from metadata" fields again
									return {
										id: field.id,
										type: field.type,
										variable: field.config.variable,
										title: field.config.title,
										friendlyValue: fieldValue,
									};
								})

								.filter(
									(field) =>
										![
											'pagebreak',
											'label',
											'hidden',
											'image',
											'textbox',
											'circle',
											'triangle',
											'star',
											'diamond',
											'asterisk',
											'plus',
											'minus',
											'line',
										].includes(field.type),
								),
						};
					})
					.filter(Boolean)
			: [],
	};

	return archivedJob;
};

const massageRawMyFile = (rawMyFile: any) => {
	const myFile: MyFile = {
		nodeId: rawMyFile.nodeId,
		type: rawMyFile.type,
		parentId: rawMyFile.parentId,
		name: rawMyFile.name,
		ownerId: rawMyFile.ownerId,
		createdAt: rawMyFile.createdAt,
		createdBy: rawMyFile.createdBy,
		updatedAt: rawMyFile.updatedAt,
		updatedBy: rawMyFile.updatedBy,
		isTrashed: rawMyFile.isTrashed,
		file: rawMyFile.file,
		hasChildDir: rawMyFile.hasChildDir ?? false,
		hasChildFile: rawMyFile.hasChildFile ?? false,
		expiresAt: rawMyFile.expiresAt,
		sharedCount: rawMyFile.sharedCount,
		favorited: rawMyFile.favorited,
		mimeType: rawMyFile.mimeType,
		size: rawMyFile.size,
	};

	return myFile;
};

const massageRawTrashedStorageNode = (rawTrashedStorageNode: any): TrashedStorageNode => {
	return {
		id: rawTrashedStorageNode.id,
		type: rawTrashedStorageNode.type,
		originalParentId: rawTrashedStorageNode.originalParentId,
		name: rawTrashedStorageNode.name,
		nameLower: rawTrashedStorageNode.nameLower,
		ownerId: rawTrashedStorageNode.ownerId,
		createdAt: rawTrashedStorageNode.createdAt,
		createdBy: rawTrashedStorageNode.createdBy,
		updatedAt: rawTrashedStorageNode.updatedAt,
		updatedBy: rawTrashedStorageNode.updatedBy,
		trashedBy: rawTrashedStorageNode.trashedBy,
		file: rawTrashedStorageNode.file,
		dir: rawTrashedStorageNode.dir,
		childDirCount: rawTrashedStorageNode.childDirCount,
		childFileCount: rawTrashedStorageNode.childFileCount,
		expiration: rawTrashedStorageNode.expiration,
		sharedCount: rawTrashedStorageNode.sharedCount,
	};
};

const massageRawMFA = (rawMFAuth: any) => {
	const mfa: MFA = {
		enabled: rawMFAuth.enabled,
		scheduled: rawMFAuth.scheduled,
		date: rawMFAuth.date,
	};

	return mfa;
};

const massageRawBSN = (rawBSN: any) => {
	const bsn: BSN = {
		serial: rawBSN.serial,
		status: rawBSN.status,
		// attributes: rawBSN.attributes,
		updatedAt: rawBSN.updatedAt,
		policyId: rawBSN.policyId,
	};
	return bsn;
};

const massageRawBsnDevices = (rawBsnDevices: any) => {
	const bsnDevices: BsnDevices = {
		serial: rawBsnDevices.Serial,
		hidden: rawBsnDevices.Hidden,
		policy: rawBsnDevices.Policy,
		policyGroupId: rawBsnDevices.PolicyGroupId ?? UNGROUPED_BSN_GROUP_ID,
		userIds: rawBsnDevices.UserIds,
		emails: rawBsnDevices.Emails,
		emailEnabled: rawBsnDevices.EmailEnabled,
		pingInterval: rawBsnDevices.PingInterval ?? -1,
	};
	return bsnDevices;
};

const massageRawBsnAvailableDevices = (rawBsnAvailableDevices: any) => {
	const bsnAvailableDevices: BsnAvailableDevices = {
		serial: rawBsnAvailableDevices.serial,
		bsnApp: rawBsnAvailableDevices.BSNApp,
		licensed: rawBsnAvailableDevices.licensed,
		bsnTenant: rawBsnAvailableDevices.BSNTenant,
	};
	return bsnAvailableDevices;
};

const massageRawBsnPolicyGroups = (rawBsnPolicyGroups: any) => {
	const bsnPolicyGroups: BsnPolicyGroups = {
		id: rawBsnPolicyGroups.Id,
		hidden: rawBsnPolicyGroups.Hidden,
		policy: rawBsnPolicyGroups.Policy,
		name: rawBsnPolicyGroups.Name,
		userIds: rawBsnPolicyGroups.UserIds,
		emails: rawBsnPolicyGroups.Emails,
		emailEnabled: rawBsnPolicyGroups.EmailEnabled,
		groupOrder: rawBsnPolicyGroups.groupOrder,
	};
	return bsnPolicyGroups;
};

const massageRawBsnDeviceOptions = (rawBsnDeviceOptions: any) => {
	const bsnDeviceOptions: BsnDeviceOptions = {
		serial: rawBsnDeviceOptions.serial,
		stratusOptIn: rawBsnDeviceOptions.stratusOptIn,
		tenantId: rawBsnDeviceOptions.tenantId,
	};
	return bsnDeviceOptions;
};

const massageRawAuthProvider = (rawAuthProvider: any) => {
	const mfpAuthProvider: authProvider = {
		id: rawAuthProvider.id,
		name: rawAuthProvider.name,
		type: rawAuthProvider.type,
		enabled: rawAuthProvider.enabled,
		configuration: rawAuthProvider.configuration,
		isDefault: rawAuthProvider.isDefault,
	};
	return mfpAuthProvider;
};

const massageRawMfpAuth = (rawMfpAuth: any) => {
	const mfpAuthSettings: MfpAuthSetting = {
		enablePublicUserAccess: rawMfpAuth.enablePublicUserAccess,
		enableQuickCopyAccess: rawMfpAuth.enableQuickCopyAccess,
		openStratusOnUnlock: rawMfpAuth.openStratusOnUnlock,
		allowUserCardLogin: rawMfpAuth.allowUserCardLogin,
		allowUserPinLogin: rawMfpAuth.allowUserPinLogin,
		noPassword: rawMfpAuth.noPassword,
		allowReleaseOnly: rawMfpAuth.allowReleaseOnly,
	};
	return mfpAuthSettings;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

// at some point this may be broken into more specific API calls

// export const getAdminedLicenseGroups = (): AppThunk => async dispatch => {
// 	try {
// 		// TODO::actually populate list
// 		const { data: rawLicenseGroups } = await Promise.resolve({ data: {} });

// 		const licenseGroups: { [id: string]: LicenseGroupData } = _.mapValues(rawLicenseGroups, massageRawLicenseGroup);

// 		dispatch({
// 			type: 'GET_ADMINED_LICENSE_GROUPS_SUCCESS',
// 			payload: {
// 				data: licenseGroups
// 			}
// 		});
// 	} catch (error) {
// 		if (error.response?.status === 401) {
// 			// if user not logged in
// 			// dispatch({
// 			// 	type: 'GET_ADMINED_LICENSE_GROUPS_SUCCESS',
// 			// 	payload: {
// 			// 		data: undefined
// 			// 	}
// 			// });
// 			return;
// 		}
// 		dispatch(appActions.handleError(error));
// 		// re-throw error for handling in <InitializeApp />
// 		throw error;
// 	}
// };

export const getManagedLicenseGroups = (): AppThunk => async (dispatch) => {
	try {
		const { data: rawLicenseGroups } = await axios.get('/api/tenants');

		const licenseGroups: { [id: string]: LicenseGroupData } = _.mapValues(rawLicenseGroups, massageRawLicenseGroup);

		dispatch({
			type: 'GET_MANAGED_LICENSE_GROUPS_SUCCESS',
			payload: {
				data: licenseGroups,
			},
		});
	} catch (error) {
		if (error instanceof AxiosError && error.response?.status === 401) {
			// if user not logged in
			dispatch({
				type: 'GET_MANAGED_LICENSE_GROUPS_SUCCESS',
				payload: {
					data: undefined,
				},
			});
			return;
		}
		dispatch(appActions.handleError(error));
		// re-throw error for handling in <InitializeApp />
		throw error;
	}
};

export const getTenantManagerGroups = (): AppThunk => async (dispatch) => {
	try {
		const { data: rawTenantLicenseGroups } = await axios.get('/api/tenants/tenant-manager-groups');
		// const tenantLicenseGroups: any[] = massageRawTenantManagerGroup(rawTenantLicenseGroups);
		const tenantGroups: { [id: string]: TenantGroup } = _.mapValues(
			_.keyBy(rawTenantLicenseGroups, 'id'),
			massageRawTenantManagerGroup,
		);
		dispatch({
			type: 'GET_TENANT_MANAGER_GROUPS_SUCCESS',
			payload: {
				data: tenantGroups,
			},
		});
	} catch (error) {
		if (error instanceof AxiosError && error.response?.status === 401) {
			// if user not logged in
			dispatch({
				type: 'GET_TENANT_MANAGER_GROUPS_SUCCESS',
				payload: {
					data: [],
				},
			});
			return;
		}
		dispatch(appActions.handleError(error));
		// re-throw error for handling in <InitializeApp />
		throw error;
	}
};

export const getEditedTenantManagerGroups =
	(id: string): AppThunk =>
	async (dispatch) => {
		const [{ data: rawTenantLicenseGroups }, { data: EmailInfo }] = await Promise.all([
			axios.get('/api/tenants/tenant-manager-groups'),
			axios.get(`/api/tenants/tenant-manager-group/${id}`),
		]);
		const tenantGroups: { [id: string]: TenantGroup } = _.mapValues(
			_.keyBy(rawTenantLicenseGroups, 'id'),
			massageRawTenantManagerGroup,
		);
		dispatch({
			type: 'GET_TENANT_MANAGER_GROUPS_SUCCESS',
			payload: {
				data: tenantGroups,
			},
		});
		dispatch({
			type: 'GET_GROUP_MANAGER_INFO_BY_ID_SUCCESS',
			payload: {
				groupId: id,
				data: EmailInfo,
			},
		});
	};

// export const forgetAdminedLicenseGroup = (licenseGroupId: LicenseGroupData['id']): AppThunk => async (
// 	dispatch,
// 	getState
// ) => {
// 	dispatch({
// 		type: 'FORGET_ADMINED_LICENSE_GROUP_SUCCESS',
// 		payload: {
// 			licenseGroupId
// 		}
// 	});
// };
export const forgetAdminedLicenseGroup =
	(licenseGroupId: LicenseGroupData['id']): AppThunk =>
	async (dispatch) => {
		try {
			await axios.delete(`/api/user/user-tenants/${licenseGroupId}`);
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const addAdminedLicenseGroup =
	(slug: LicenseGroupData['slug']): AppThunk =>
	async (dispatch, _getState) => {
		try {
			await axios.patch('/api/user/user-tenants', { slug });
		} catch (error) {
			if (error instanceof AxiosError) {
				if (error?.response?.status === 404) {
					dispatch(appActions.alert('license group to add not found', 'error'));
					throw error;
				} else if (error?.response?.status === 409) {
					dispatch(appActions.alert('license group already added', 'error'));
					throw error;
				}
			}
			dispatch(appActions.handleError(error));
			throw error;
		}
	};

export const getSelectedLicenseGroupData =
	(resources: LicenseGroupResource[], successFn?: () => void, count = 0): AppThunk =>
	async (dispatch, getState) => {
		const state = getState();
		const licenseGroupId = getSelectedLicenseGroupId(state);
		const profile = getProfile(state);
		const wfxApiUrl = getWfxUrl(profile?.awsRegion);
		const formsApiUrl = getFormsUrl(profile?.awsRegion);
		const r2mApiUrl = getR2mUrl(profile?.awsRegion);
		const jntApiUrl = getJntUrl(profile?.awsRegion);
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const fileServiceUrl = getFileserviceUrl(profile?.awsRegion);

		const resourceCheck = (resource: LicenseGroupResource) => {
			if (resources.length === 0) {
				return true;
			}
			return resources.includes(resource);
		};

		const schemaCheck = (serverData: any, schemaFn: Function, msg: string) => {
			if (serverData && !schemaFn(serverData)) {
				console.log(msg.toUpperCase());
				console.log('DEBUG: Schema validation failed for:', msg);
				console.log('DEBUG: Data that failed validation:', JSON.stringify(serverData, null, 2));
			}
		};

		try {
			const [
				{ data: rawLicenseGroup },
				{ data: rawUsers },
				{ data: rawPendingUsers },
				{ data: rawUserGroups },
				{ data: rawDevices },
				{ data: rawDeviceGroups },
				{ data: rawInputNodes },
				{ data: rawDirectPrintAgents },
				{ data: rawRoles },
				{ data: rawAdminInfo },
				{ data: rawWorkflows },
				{ data: rawSampleWorkflows },
				{ data: rawWorkflowGroups },
				{ data: rawForms }, // Added this line to get the forms data from the new endpoint
				{ data: rawFormGroups }, // Added this line to get the form groups data from the new endpoint
				{ data: rawEntitlements },
				{ data: rawApps },
				{ data: rawAppGroups },
				{ data: rawPrints },
				{ data: printOrder },
				{ data: rawPrintGroups },
				{ data: managedPrintOrder },
				{ data: rawScheduledReports },
				{ data: rawNotifications },
				{ data: rawNotificationGroups },
				{ data: rawJobNames },
				{ data: rawJobNameGroups },
				{ data: rawMFA },
				{ data: rawBSN },
				{ data: rawBsnDevices },
				{ data: rawBsnAvailableDevices },
				{ data: rawBsnPolicyGroups },
				{ data: rawBsnDeviceOptions },
				{ data: rawAuthProviders },
				{ data: rawMfpAuth },
				{ data: rawJobs },
				{ data: rawArchivedJobs },
				{ data: rawMyFiles },
				{ data: rawTrashedStorageNodes },
				// { data: testData }
			] = await Promise.all([
				resourceCheck('license')
					? axios.get(`/api/tenants/${licenseGroupId}/details`)
					: {
							data: undefined as any,
						},
				resourceCheck('users')
					? axios.get(`/api/tenants/${licenseGroupId}/users?hidden=true`)
					: {
							data: undefined as any,
						},
				resourceCheck('pending users')
					? axios.get(`/api/tenants/${licenseGroupId}/pendingUsers?hidden=true`)
					: {
							data: undefined as any,
						},
				resourceCheck('user groups')
					? axios.get(`/api/tenants/${licenseGroupId}/userGroups`)
					: {
							data: undefined as any,
						},
				resourceCheck('devices')
					? axios.get(`/api/tenants/${licenseGroupId}/devices?includeExternal=true`)
					: {
							data: undefined as any,
						},
				resourceCheck('device groups')
					? axios.get(`/api/tenants/${licenseGroupId}/deviceGroups`)
					: {
							data: undefined as any,
						},
				resourceCheck('input nodes')
					? axios.get(`/api/tenants/${licenseGroupId}/inputNodes`)
					: {
							data: undefined as any,
						},
				resourceCheck('direct print agents')
					? axios.get(`/api/tenants/${licenseGroupId}/direct-print-agents`).catch((error) => {
							return { data: { things: [] } };
						})
					: {
							data: undefined as any,
						},
				resourceCheck('roles')
					? axios.get(`/api/tenants/${licenseGroupId}/roles`)
					: {
							data: undefined as any,
						},
				resourceCheck('admin info')
					? axios.get(`/api/tenants/${licenseGroupId}/adminInfo`)
					: {
							data: undefined as any,
						},
				resourceCheck('workflows')
					? axios.get(`${wfxApiUrl}/api/wfx`).catch((error) => {
							return { data: [] };
						})
					: { data: undefined as any },
				resourceCheck('sample workflows')
					? axios.get(`${wfxApiUrl}/api/samples`).catch((error) => {
							return { data: [] };
						})
					: { data: undefined as any },
				resourceCheck('workflow groups')
					? axios.get(`${wfxApiUrl}/api/groups`).catch((error) => {
							return { data: [] };
						})
					: { data: undefined as any },
				// Comment above and uncomment below to test groups sharing
				// axios.get(`https://feature-groups.wfx.stratus.lol/api/groups`),
				resourceCheck('forms') ? axios.get(`${formsApiUrl}/api/forms`) : { data: undefined as any },
				resourceCheck('form groups') ? axios.get(`${formsApiUrl}/api/groups`) : { data: undefined as any },
				resourceCheck('entitlements') ? axios.get(`/api/user/entitlements`) : { data: undefined as any },
				resourceCheck('apps') ? axios.get('/api/app') : { data: undefined as any },
				resourceCheck('app groups') ? axios.get('/api/app/app-group') : { data: undefined as any },
				resourceCheck('prints')
					? axios.get(`${r2mApiUrl}/api/print-job/get/user?adminSite=true`)
					: { data: undefined as any },
				resourceCheck('prints')
					? axios.get(`/api/user/${licenseGroupId}/group-order/print`).catch((_error) => ({ data: [] }))
					: { data: undefined as any },
				resourceCheck('print groups')
					? axios.get(`${r2mApiUrl}/api/print-queue/get/user`)
					: { data: undefined as any },
				resourceCheck('print groups')
					? axios
							.get(`/api/user/${licenseGroupId}/group-order/managedPrint`)
							.catch((_error) => ({ data: [] }))
					: { data: undefined as any },
				resourceCheck('scheduledReports')
					? axios.get(`${r2mApiUrl}/api/scheduled-reports/get/user`).catch((_error) => ({ data: [] }))
					: { data: undefined as any },
				resourceCheck('notifications') ? axios.get('/api/notification') : { data: undefined as any },
				resourceCheck('notification groups') ? { data: [] } : { data: undefined as any },
				resourceCheck('job names')
					? axios.get(`${jntApiUrl}/api/portal/jnt/${licenseGroupId}`).catch((error) => {
							return { data: [] };
						})
					: {
							data: undefined as any,
						},
				resourceCheck('job name groups')
					? axios.get(`${jntApiUrl}/api/portal/jntg/${licenseGroupId}`).catch((error) => {
							return { data: [] };
						})
					: {
							data: undefined as any,
						},
				resourceCheck('mfa')
					? axios.get(`/api/mfa/${licenseGroupId}`).catch((error) => {
							return { data: [] };
						})
					: { data: undefined as any },
				resourceCheck('bsn')
					? axios.post(`/api/bsn/status/devices`)
					: {
							data: undefined as any,
						},
				resourceCheck('bsn devices')
					? axios.get(`/api/bsn/devices/${licenseGroupId}`)
					: {
							data: undefined as any,
						},
				resourceCheck('bsn available devices')
					? axios.get(`/api/bsn/available-devices/${licenseGroupId}`)
					: {
							data: undefined as any,
						},
				resourceCheck('bsn policy groups')
					? axios.get(`/api/bsn/policy-groups/${licenseGroupId}`)
					: {
							data: undefined as any,
						},
				resourceCheck('bsn device options')
					? axios.get(`/api/bsn/device-options/${licenseGroupId}`)
					: {
							data: undefined as any,
						},
				resourceCheck('auth providers')
					? axios.get(`/api/tenants/${licenseGroupId}/auth-providers`).catch((error) => {
							return { data: [] as any };
						})
					: {
							data: undefined as any,
						},
				resourceCheck('mfp auth')
					? axios.get(`/api/tenants/mfp-auth`)
					: {
							data: undefined as any,
						},
				resourceCheck('jobs')
					? axios
							.get(`${jobsApiUrl}/people/jobs/user`, {
								params: {
									userID: profile.id,
									active: true,
								},
							})
							.catch((error) => {
								return { data: { jobs: [] } };
							})
					: { data: undefined as any },
				resourceCheck('archived jobs')
					? (async () => {
							try {
								// Fetch all pages of archived jobs to work around 6MB Lambda response limit
								let allArchives: any[] = [];
								let lastKey: string | null = null;
								const limit = 50;

								do {
									const params = new URLSearchParams({ userID: profile.id, limit: limit.toString() });
									if (lastKey) {
										params.append('lastKey', lastKey);
									}

									// eslint-disable-next-line no-await-in-loop
									const response = await axios.get(
										`${jobsApiUrl}/people/archive/user?${params.toString()}`,
									);
									const { archives = [], lastKey: nextKey } = response.data;

									allArchives = allArchives.concat(archives);
									lastKey = nextKey;
								} while (lastKey);

								return { data: { archives: allArchives } };
							} catch (error) {
								console.error('Failed to fetch archived jobs:', error);
								return { data: { archives: [] } };
							}
						})()
					: { data: undefined as any },
				resourceCheck('my files')
					? (async () => {
							try {
								let allItems: any[] = [];
								let nextCursor: string | undefined;
								let parentId: string | undefined;
								const limit = 50;

								do {
									const params = new URLSearchParams({ limit: limit.toString() });
									if (nextCursor) {
										params.append('next', nextCursor);
									}

									// eslint-disable-next-line no-await-in-loop
									const response = await axios.get(
										`${fileServiceUrl}/v1/nodes/home?${params.toString()}`,
										{ headers: { 'x-tenant-id': licenseGroupId } },
									);

									if (!parentId) {
										parentId = response.data.parentId;
									}
									allItems = allItems.concat(response.data.items ?? []);
									nextCursor = response.data.next;
								} while (nextCursor);

								return { data: { parentId, items: allItems } };
							} catch (error) {
								console.error('Failed to fetch my files:', error);
								return { data: undefined as any };
							}
						})()
					: {
							data: undefined as any,
						},
				resourceCheck('trashed-storage-nodes')
					? (async () => {
							try {
								// Fetch all pages of trashed files to work around 6MB Lambda response limit
								let allTrashedNodes: any[] = [];
								let lastKey: string | null = null;
								const limit = 50;

								do {
									const params = new URLSearchParams({ limit: limit.toString() });
									if (lastKey) {
										params.append('next', lastKey);
									}

									const response = await axios.get(
										`${fileServiceUrl}/v1/nodes/trash?${params.toString()}`,
										{ headers: { 'x-tenant-id': licenseGroupId } },
									);
									const { nodes: trashedNodes = [], lastKey: nextKey } = response.data;

									allTrashedNodes = allTrashedNodes.concat(trashedNodes);
									lastKey = nextKey;
								} while (lastKey);

								return { data: { trashedStorageNodes: allTrashedNodes } };
							} catch (error) {
								console.error('Failed to fetch archived jobs:', error);
								return { data: { trashedStorageNodes: [] } };
							}
						})()
					: { data: undefined as any },
			]);

			// console.log({ rawJobs, rawQueues, rawInbox });

			schemaCheck(rawLicenseGroup, licenseSchema, 'License data does not match the schema');
			schemaCheck(rawUsers, usersSchema, 'Users data does not match the schema');
			schemaCheck(rawPendingUsers, pendingUsersSchema, 'Pending users data does not match the schema');
			schemaCheck(rawUserGroups, userGroupsSchema, 'User groups data does not match the schema');
			schemaCheck(rawDevices, devicesSchema, 'Devices data does not match the schema');
			schemaCheck(rawDeviceGroups, deviceGroupsSchema, 'Device groups data does not match the schema');
			schemaCheck(rawInputNodes, inputNodesSchema, 'Input nodes data does not match the schema');
			schemaCheck(rawRoles, rolesSchema, 'Roles data does not match the schema');
			schemaCheck(rawAdminInfo, adminInfoSchema, 'Admin info data does not match the schema');
			schemaCheck(rawForms, formsSchema, 'Forms data does not match the schema');
			schemaCheck(rawFormGroups, formGroupsSchema, 'Form groups data does not match the schema');
			schemaCheck(rawSampleWorkflows, sampleWorkflowsSchema, 'Sample workflows data does not match the schema');
			schemaCheck(rawWorkflows, workflowsSchema, 'Workflows data does not match the schema');
			schemaCheck(rawWorkflowGroups, workflowGroupsSchema, 'Workflow groups data does not match the schema');
			schemaCheck(rawEntitlements, entitlementsSchema, 'Entitlements data does not match the schema');
			schemaCheck(rawApps, appsSchema, 'Apps data does not match the schema');
			schemaCheck(rawAppGroups, appGroupsSchema, 'App groups data does not match the schema');
			// schemaCheck(rawPrints, appsSchema, 'Prints data does not match the schema');
			// schemaCheck(rawPrintGroups, appGroupsSchema, 'Print groups data does not match the schema');
			schemaCheck(rawJobNames, jobNamesSchema, 'Job names data does not match the schema');
			schemaCheck(rawJobNameGroups, jobNameGroupsSchema, 'Job name groups data does not match the schema');
			schemaCheck(rawBSN, BSNSchema, 'bSN data does not match the schema');
			schemaCheck(
				rawTrashedStorageNodes,
				trashedStorageNodeSchema,
				'Trashed Storage Node data does not match the schema',
			);

			// HACK::apply various hacks
			const licenseGroup = rawLicenseGroup ? massageRawLicenseGroup(rawLicenseGroup) : undefined;
			const users: { [id: string]: User } | undefined = rawUsers
				? _.mapValues(rawUsers, massageRawUser)
				: undefined;
			const pendingUsers: { [id: string]: PendingUser } | undefined = rawPendingUsers
				? _.mapValues(rawPendingUsers, massageRawPendingUser)
				: undefined;
			const userGroups: { [id: string]: UserGroup } | undefined = rawUserGroups
				? _.mapValues(rawUserGroups, massageRawUserGroup)
				: undefined;
			const devices: { [id: string]: Device } | undefined = rawDevices
				? _.mapValues(rawDevices, massageRawDevice)
				: undefined;
			if (devices && devices.ADXV013000009) {
				console.log(
					'DEBUG: Processed device data for ADXV013000009:',
					JSON.stringify(devices.ADXV013000009, null, 2),
				);
			}
			const deviceGroups: { [id: string]: DeviceGroup } | undefined = rawDeviceGroups
				? _.mapValues(rawDeviceGroups, massageRawDeviceGroup)
				: undefined;
			const inputNodes: { [id: string]: InputNode } | undefined = rawInputNodes
				? _.mapValues(rawInputNodes, massageRawInputNode)
				: undefined;
			const directPrintAgents: { [id: string]: DirectPrintAgent } | undefined = rawDirectPrintAgents?.things
				? _.mapValues(
						_.keyBy(rawDirectPrintAgents.things, (thing) => thing.attributes.AgentId),
						massageRawDirectPrintAgent,
					)
				: undefined;
			const roles: { [id: string]: Role } | undefined = rawRoles
				? _.mapValues(rawRoles, massageRawRole)
				: undefined;
			const contactInfo: { [id: string]: AdminInfo } | undefined = rawAdminInfo
				? _.mapValues(rawAdminInfo, massageRawAdminInfo)
				: undefined;
			// TODO::add back real data (and remove weird conditional hack) when endpoints are complete
			const forms: { [id: string]: FormOverview } | undefined = rawForms ? massageRawForm(rawForms) : undefined;

			const formGroups: { [id: string]: FormGroup } | undefined = rawFormGroups
				? massageRawFormGroup(rawFormGroups)
				: undefined;
			const sampleWorkflows: { [id: string]: SampleWorkflow } | undefined = rawSampleWorkflows
				? _.mapValues(_.keyBy(rawSampleWorkflows, 'id'), (rawSampleWorkflow) =>
						massageRawSampleWorkflow(rawSampleWorkflow),
					)
				: undefined;
			const workflows: { [id: string]: Workflow } | undefined = rawWorkflows
				? _.mapValues(_.keyBy(rawWorkflows, 'id'), (rawWorkflow) =>
						massageRawWorkflow(rawWorkflow, profile.id, profile?.awsRegion),
					)
				: undefined;
			const workflowGroups: { [id: string]: WorkflowGroup } | undefined = rawWorkflowGroups
				? _.mapValues(_.keyBy(rawWorkflowGroups, 'id'), (rawWorkflowGroup) =>
						massageRawWorkflowGroup(rawWorkflowGroup, profile.id),
					)
				: undefined;
			// const workflowGroups: { [id: string]: WorkflowGroup } = localStorage.getItem('mock')
			// 	? _.mapValues(rawWorkflowGroups, massageRawWorkflowGroup)
			// 	: {};
			const entitlements:
				| {
						permissions: Role['permissions'];
						features: { [feature: string]: boolean };
				  }
				| undefined = rawEntitlements ? massageRawEntitlements(rawEntitlements) : undefined;
			const apps: { [id: string]: App } | undefined = rawApps
				? _.mapValues(_.keyBy(rawApps, 'id'), (rawApp) => massageRawApp(rawApp, profile.id))
				: undefined;
			const appGroups: { [id: string]: AppGroup } | undefined = rawAppGroups
				? _.mapValues(_.keyBy(rawAppGroups, 'id'), (rawAppGroup) => massageRawAppGroup(rawAppGroup))
				: undefined;
			const prints: { [id: string]: Print } | undefined = rawPrints
				? _.mapValues(_.keyBy(rawPrints, 'id'), (rawPrint) => massageRawPrint(rawPrint, profile.id))
				: undefined;
			const printGroups: { [id: string]: PrintGroup } | undefined = rawPrintGroups
				? _.mapValues(_.keyBy(rawPrintGroups, 'id'), (rawPrintGroup) =>
						massageRawPrintGroup(rawPrintGroup, profile.id),
					)
				: undefined;
			const scheduledReports: { [id: string]: ScheduledReport } | undefined = rawScheduledReports
				? _.mapValues(_.keyBy(rawScheduledReports, 'id'), (rawScheduledReport) =>
						massageRawScheduledReport(rawScheduledReport),
					)
				: undefined;
			const notifications: { [id: string]: Notification } | undefined = rawNotifications
				? _.mapValues(_.keyBy(rawNotifications, 'id'), (rawNotification) =>
						massageRawNotification(rawNotification),
					)
				: undefined;
			const notificationGroups: { [id: string]: NotificationGroup } | undefined = rawNotificationGroups
				? _.mapValues(_.keyBy(rawNotificationGroups, 'id'), (rawNotificationGroup) =>
						massageRawNotificationGroup(rawNotificationGroup),
					)
				: undefined;
			const jobNames: { [id: string]: JobName } | undefined = rawJobNames
				? _.mapValues(_.keyBy(rawJobNames, 'id'), (rawJobName) => massageRawJobName(rawJobName))
				: undefined;
			const jobNameGroups: { [id: string]: JobNameGroup } | undefined = rawJobNameGroups
				? _.mapValues(_.keyBy(rawJobNameGroups, 'id'), (rawJobNameGroup) =>
						massageRawJobNameGroup(rawJobNameGroup),
					)
				: undefined;
			const mfa = rawMFA ? massageRawMFA(rawMFA) : undefined;
			const bsn: { [id: string]: BSN } | undefined = rawBSN ? _.mapValues(rawBSN, massageRawBSN) : undefined;
			const bsnDevices: { [id: string]: BsnDevices } | undefined = rawBsnDevices
				? _.mapValues(rawBsnDevices, massageRawBsnDevices)
				: undefined;
			const bsnAvailableDevices = rawBsnAvailableDevices
				? _.mapValues(rawBsnAvailableDevices, massageRawBsnAvailableDevices)
				: undefined;
			const bsnPolicyGroups: { [id: string]: BsnPolicyGroups } | undefined = rawBsnPolicyGroups
				? _.mapValues(rawBsnPolicyGroups, massageRawBsnPolicyGroups)
				: undefined;
			const bsnDeviceOptions: { [id: string]: BsnDeviceOptions } | undefined = rawBsnDeviceOptions
				? _.mapValues(rawBsnDeviceOptions, massageRawBsnDeviceOptions)
				: undefined;
			const authProviders: { [id: string]: authProvider } | undefined = rawAuthProviders
				? _.mapValues(rawAuthProviders, massageRawAuthProvider)
				: undefined;
			const mfpAuthSettings = rawMfpAuth ? massageRawMfpAuth(rawMfpAuth) : undefined;
			const jobs = rawJobs?.jobs ? groupByID(rawJobs.jobs) : undefined;
			const archivedJobs = rawArchivedJobs?.archives
				? _.mapValues(
						_.keyBy(rawArchivedJobs.archives, (archive) => archive.SK.split(':').pop()),
						massageRawArchivedJob,
					)
				: undefined;
			const myFiles = rawMyFiles?.items
				? _.mapValues(
						_.keyBy(rawMyFiles.items, (item: any) => item.nodeId),
						massageRawMyFile,
					)
				: undefined;
			const trashedStorageNodes = rawTrashedStorageNodes?.trashedStorageNodes
				? _.mapValues(
						_.keyBy(
							rawTrashedStorageNodes.trashedStorageNodes,
							(trashedStorageNode) => trashedStorageNode.id,
						),
						massageRawTrashedStorageNode,
					)
				: undefined;
			const myFilesRootId: string | undefined = rawMyFiles?.parentId;

			if (devices && devices.ADXV013000009) {
				console.log(
					'DEBUG: About to dispatch device to Redux:',
					JSON.stringify(devices.ADXV013000009, null, 2),
				);
			}
			dispatch({
				type: 'GET_LICENSE_GROUP_DATA_SUCCESS',
				payload: {
					licenseGroupId,
					data: {
						details: licenseGroup,
						contactInfo,
						users,
						pendingUsers,
						userGroups,
						devices,
						deviceGroups,
						inputNodes,
						directPrintAgents,
						roles,
						forms,
						formGroups: formGroups
							? {
									...formGroups,
									[SAMPLE_FORMS_GROUP_ID as string]: SAMPLE_FORMS_GROUP,
								}
							: undefined,
						workflows,
						sampleWorkflows,
						workflowGroups: workflowGroups
							? {
									...workflowGroups,
									[SAMPLE_WORKFLOWS_GROUP_ID as string]: SAMPLE_WORKFLOWS_GROUP,
								}
							: undefined,
						entitlements,
						apps,
						appGroups,
						prints,
						printGroups,
						scheduledReports,
						notifications,
						notificationGroups,
						jobNames,
						jobNameGroups,
						jobs,
						mfa,
						bsn,
						bsnDevices,
						bsnAvailableDevices,
						bsnPolicyGroups,
						bsnDeviceOptions,
						authProviders,
						mfpAuthSettings,
						archivedJobs,
						myFiles,
						trashedStorageNodes,
						myFilesRootId,
					},
				},
			});
			if (successFn) successFn();

			if (printOrder?.length) {
				dispatch({
					type: 'GET_PRINT_GROUP_ORDER_SUCCESS',
					payload: {
						order: printOrder,
						licenseGroupId,
					},
				});
			}
			if (managedPrintOrder?.length) {
				dispatch({
					type: 'GET_MANAGED_PRINT_GROUP_ORDER_SUCCESS',
					payload: {
						order: managedPrintOrder,
						licenseGroupId,
					},
				});
			}
		} catch (error) {
			console.error('getSelectedLicenseGroupData error:', error);
			if (error instanceof AxiosError && error.response?.status === 403) {
				// re-throw error for handling in <LicenseGroupPageWrapper /> if 403
				throw error;
			}
			// try running this one more time if I get a `401`
			if (error instanceof AxiosError && error.response?.status === 401 && count === 0) {
				await dispatch(profileActions.getProfile());
				// DEV NOTE::I have no idea if there could be an event cycle issue here but...I'm doing this to maybe ensure that the profile is updated before trying again
				setTimeout(() => {
					dispatch(getSelectedLicenseGroupData(resources, successFn, count + 1));
				}, 0);
			}
			dispatch(appActions.handleError(error));
		}
	};

export const getTenantId = (): AppThunk<Promise<string>> => async (_dispatch, getState) => {
	const id = getSelectedLicenseGroupId(getState());
	return id;
};

export const getTenantById =
	(id: string): AppThunk =>
	async (dispatch, _getState) => {
		try {
			const {
				data: { adminInfo },
			} = await axios.get(`/api/tenants/contact-info/${id}`);

			dispatch({
				type: 'GET_TENANT_BY_ID_SUCCESS',
				payload: {
					id,
					adminInfo,
				},
			});
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const resetPasswordMessage =
	(username: string, tenantId: string, successFn?: (email: string) => void): AppThunk =>
	async (dispatch, _getState) => {
		const data = { username, tenantId };
		try {
			const response = await axios.post('/api/tenants/reset-message', data);
			dispatch(appActions.alert('reset password message sent', 'success'));
			if (successFn) successFn(response?.data?.email);
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const resetLoginPassword =
	(code: string, password: string, tenantId: string, username: string, successFn?: () => void): AppThunk =>
	async (dispatch, _getState) => {
		const data = { code, password, tenantId, username };
		try {
			await axios.post('/api/tenants/reset-password', data);
			dispatch(appActions.alert('password reset', 'success'));
			if (successFn) successFn();
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const customLogin =
	(username: string, password: string, tenantId: string, failFn?: () => void): AppThunk =>
	async (dispatch, getState) => {
		console.log('customLogin: starting login attempt', {
			username,
			hasPassword: !!password,
			tenantId,
			currentUrl: window.location.href,
			timestamp: new Date().toISOString(),
		});

		const data = { username, password, tenantId };
		try {
			console.log('customLogin: making request to /api/tenants/custom-login');
			await axios.post('/api/tenants/custom-login', data);

			try {
				await dispatch(profileActions.getProfile());
			} catch (profileError) {
				console.log('customLogin: profile fetch failed, but login succeeded', {
					error: profileError instanceof Error ? profileError.message : String(profileError),
				});
			}

			dispatch(appActions.alert('login success', 'success'));

			const urlParams = new URLSearchParams(window.location.search);
			const redirectTo = urlParams.get('redirect') || '/';
			console.log('customLogin: redirecting to', redirectTo);
			window.location.href = redirectTo;
		} catch (error) {
			console.error('customLogin: login failed', {
				error: error instanceof Error ? error.message : String(error),
				status: (error as any).response?.status,
				statusText: (error as any).response?.statusText,
				username,
				timestamp: new Date().toISOString(),
			});

			dispatch(appActions.handleError(error));
			if (failFn) failFn();
		}
	};

export const editContactInfo =
	(data: AdminInfo, id: string): AppThunk =>
	async (dispatch, _getState) => {
		try {
			await axios.patch(`/api/tenants/${id}/contact-data`, data);
			dispatch(appActions.alert('contact info updated', 'success'));
			dispatch(getManagedLicenseGroups());
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const editSelectedTenant =
	({
		name,
		timezone,
		language,
	}: {
		name?: LicenseGroupData['name'];
		timezone?: LicenseGroupData['timezone'];
		language?: LicenseGroupData['language'];
	}): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		const data = {
			name,
			timezone,
			language,
		};

		try {
			const response = await axios.patch(`/api/tenants/${tenantId}/settings`, data);
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('failed to update settings', 'warning'));
			} else {
				dispatch(appActions.alert('settings updated', 'success'));
			}
			dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const modifySelectedTenant =
	({ newPublicId, newCapacity }: { newPublicId?: PublicId; newCapacity: number }): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		try {
			await axios.post(`/api/tenants/${tenantId}/registration`, {
				capacity: newCapacity,
				...(newPublicId && { catalogItemId: newPublicId }),
			});
			dispatch(appActions.alert('license plan updated', 'success'));
			dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
		} catch (error) {
			if (error instanceof AxiosError) {
				if (error?.response?.data?.message === 'Invalid plan upgrade') {
					dispatch(appActions.alert('license plan invalid', 'warning'));
				} else if (error?.response?.data?.message === 'MAX_CAPACITY_EXCEEDED') {
					dispatch(appActions.alert('license count warning', 'warning'));
				} else {
					dispatch(appActions.handleError(error));
				}
			} else {
				dispatch(appActions.handleError(error));
			}
			dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
		}
	};

export const configureTenant =
	(
		data: {
			// roles: string[];
			name: string;
			region: string;
			localAdminEmail?: string;
			slug: string;
			authMethod: string;
			fromManager?: boolean;
			userRoles: string[]; // tenant-admin or tenant-manager,
			tenantGroupName?: string;
			tenantParentGroupId?: string;
			metadataUrl: string;
		},
		id: string,
		token?: string,
	): AppThunk =>
	async (dispatch, getState) => {
		// console.log('configureTenant', data, id, token);
		const {
			name,
			region,
			localAdminEmail,
			slug,
			authMethod,
			fromManager = false,
			userRoles,
			tenantGroupName,
			tenantParentGroupId,
			metadataUrl,
		} = data;
		try {
			const payload = {
				name,
				region,
				localAdminEmail,
				slug,
				authMethod,
				fromManager,
				userRoles,
				tenantGroupName,
				tenantParentGroupId,
				metadataUrl,
			};

			await axios.patch(`/api/tenants/${id}/configure${token ? `?token=${token}` : ''}`, payload);
			// dispatch(appActions.alert('license plan updated', 'success'));
			dispatch(getManagedLicenseGroups());
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const copyTenantData =
	({ targetTenantId, copyDataTypes }: { targetTenantId: string; copyDataTypes: any }): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		try {
			await axios.patch(`/api/v1/tenant/${tenantId}/copy`, {
				targetTenantId,
				copyDataTypes,
			});
			dispatch(appActions.alert('Tenant data copied', 'success'));
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const inviteManager =
	({
		managerEmail,
		tenantId,
		onSuccess = () => {},
		onError = () => {},
	}: {
		managerEmail: string;
		tenantId: string;
		onSuccess?: () => void;
		onError?: () => void;
	}): AppThunk =>
	async (dispatch, getState) => {
		try {
			await axios.patch(`/api/tenants/${tenantId}/invite-manager`, { managerEmail });
			dispatch(getTenantGroupInfo());
			dispatch(appActions.alert('tenants:invite group manager', 'success'));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error));
			onError();
		}
	};

export const resendManagerInvite =
	({
		managerEmail,
		tenantId,
		onSuccess = () => {},
		onError = () => {},
	}: {
		managerEmail: string;
		tenantId: string;
		onSuccess?: () => void;
		onError?: () => void;
	}): AppThunk =>
	async (dispatch, getState) => {
		try {
			await axios.patch(`/api/tenants/${tenantId}/resend-manager-invite`, { managerEmail });
			dispatch(getTenantGroupInfo());
			dispatch(appActions.alert('tenants:invite group manager', 'success'));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error));
			onError();
		}
	};

export const cancelManagerInvite =
	({
		managerEmail,
		tenantId,
		onSuccess = () => {},
		onError = () => {},
	}: {
		managerEmail: string;
		tenantId: string;
		onSuccess?: () => void;
		onError?: () => void;
	}): AppThunk =>
	async (dispatch, getState) => {
		try {
			await axios.patch(`/api/tenants/${tenantId}/cancel-manager-invite`, { managerEmail });
			dispatch(getTenantGroupInfo());
			dispatch(appActions.alert('tenants:invite canceled', 'success'));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error));
			onError();
		}
	};

export const inviteAdmin =
	(data: {
		adminEmail: string;
		tenantId: string;
		managerInfo: { email: string; firstName: string; lastName: string };
		parentGroupId: string;
		parentGroupName: string;
	}): AppThunk =>
	async (dispatch, getState) => {
		const { adminEmail, tenantId, managerInfo, parentGroupId, parentGroupName } = data;
		// const tenantId = getSelectedLicenseGroupId(getState());
		// const { name } = getSelectedLicenseGroupData(getState());
		try {
			await axios.patch(`/api/tenants/${tenantId}/invite-admin`, {
				adminEmail,
				managerInfo,
				parentGroupId,
				parentGroupName,
			});
			dispatch(appActions.alert('tenants:invite tenant admin', 'success'));
			dispatch(getManagedLicenseGroups());
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};
export const resendAdminInvite =
	(data: {
		adminEmail: string;
		tenantId: string;
		managerInfo: { email: string; firstName: string; lastName: string };
	}): AppThunk =>
	async (dispatch, getState) => {
		const { adminEmail, tenantId, managerInfo } = data;
		try {
			await axios.patch(`/api/tenants/${tenantId}/resend-invite`, { adminEmail, managerInfo });
			dispatch(appActions.alert('tenants:invite tenant admin', 'success'));
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const inviteTenantGroupManager =
	(managerEmail: string, tenantGroupId: string, groupName: string): AppThunk =>
	async (dispatch, getState) => {
		try {
			await axios.patch(`/api/tenants/tenant-group/${tenantGroupId}/invite-manager`, { managerEmail, groupName });
			dispatch(appActions.alert('tenants:invite group manager', 'success'));
			dispatch(getTenantManagerGroupById(tenantGroupId));
		} catch (error) {
			dispatch(appActions.alert('user:error:invite user', 'warning'));
		}
	};

export const resendGroupManagerInvite =
	(groupId: string, managerEmail: string, groupName: string): AppThunk =>
	async (dispatch, getState) => {
		try {
			await axios.patch(`/api/tenants/tenant-group/${groupId}/resend-invite`, { managerEmail, groupName });
			dispatch(appActions.alert('tenants:invite group manager', 'success'));
			dispatch(getTenantManagerGroupById(groupId));
		} catch (error) {
			dispatch(appActions.alert('user:error:resend invite', 'warning'));
		}
	};

export const cancelGroupManagerInvite =
	(groupId: string, managerEmail: string): AppThunk =>
	async (dispatch, getState) => {
		try {
			await axios.patch(`/api/tenants/tenant-group/${groupId}/cancel-invite`, { managerEmail });
			dispatch(appActions.alert('tenants:invite canceled', 'success'));
			dispatch(getTenantManagerGroupById(groupId));
		} catch (error) {
			dispatch(appActions.handleError(error));
			dispatch(appActions.alert('user:error:cancel invite', 'warning'));
		}
	};

export const removeGroupManager =
	(groupId: string, managerUserId: string): AppThunk =>
	async (dispatch, getState) => {
		try {
			await axios.patch(`/api/tenants/tenant-group/${groupId}/remove-manager`, {
				managerUserId,
			});
			dispatch(appActions.alert('tenants:manager removed', 'success'));
			dispatch(getTenantManagerGroupById(groupId));
		} catch (error) {
			dispatch(appActions.handleError(error));
			dispatch(appActions.alert('user:error:remove user', 'warning'));
		}
	};

export const verifyToken =
	(token: string): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		try {
			const response = await axios.get(`/api/tenants/${tenantId}/tenant/${token}`);
			if (response.data === 'Token verified!') {
				dispatch({
					type: 'TOKEN_VERIFICATION_SUCCESS',
					payload: { licenseGroupId: tenantId, tokenStatus: 'TOKEN_VERIFIED' },
				});
				dispatch(appActions.alert('Token verified', 'success'));
				return;
			}
			// dispatch({ type: 'TOKEN_VERIFICATION_SUCCESS', payload: false });
			dispatch({
				type: 'TOKEN_VERIFICATION_FAIL',
				payload: { licenseGroupId: tenantId, tokenStatus: 'TOKEN_ERROR' },
			});

			dispatch(appActions.alert('Token invalid', 'warning'));
		} catch (error) {
			dispatch(appActions.handleError(error));
		}
	};

export const upgradeDemoToProductionLicense =
	({
		purchaseCode,
		orderNumber,
		userId,
		// claimManager
		onSuccess = () => {},
		onError = () => {},
	}: {
		purchaseCode: string;
		orderNumber: string;
		userId: string;
		// claimManager: boolean;
		onSuccess?: Function;
		onError?: Function;
	}): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());
		purchaseCode = purchaseCode.replace(/-/g, '');

		try {
			const { data } = await axios.patch(`/api/tenants/${tenantId}/upgrade`, {
				purchaseCode,
				orderNumber,
				userId,
				// claimManager
			});

			if (data.success && data.unassigned) {
				dispatch(
					appActions.alert('License upgraded, but licenses have been unassigned from devices', 'warning'),
				);
			} else if (data.success && !data.unassigned) {
				dispatch(appActions.alert('License upgraded', 'success'));
			}
			onSuccess();
			dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
			dispatch(getTenantGroupInfo());
		} catch (error) {
			if (error instanceof AxiosError) {
				if (error.response?.status && error.response.status !== 500) {
					if (error.response.status === 403) {
						dispatch(appActions.alert('purchase code:stratus error', 'warning'));
					} else {
						dispatch(appActions.alert('Purchase code invalid', 'warning'));
					}
				} else {
					dispatch(appActions.handleError(error));
				}
			} else {
				dispatch(appActions.handleError(error));
			}
			onError();
		}
	};

export const upgradeTermLicense =
	({
		capacity,
		userCapacity,
		r2mCapacity,
		expirationDate,
		userExpirationDate,
		r2mExpirationDate,
		purchaseCode,
		userId,
		callbackFn,
	}: {
		capacity: number;
		userCapacity: number;
		r2mCapacity: number;
		expirationDate: number;
		userExpirationDate: number;
		r2mExpirationDate: number;
		purchaseCode: string;
		userId: string;
		callbackFn?: Function;
	}): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());
		purchaseCode = purchaseCode.replace(/-/g, '');
		let success = false;

		try {
			const response = await axios.patch(`/api/tenants/${tenantId}/renew-license`, {
				capacity,
				userCapacity,
				r2mCapacity,
				expirationDate,
				userExpirationDate,
				r2mExpirationDate,
				purchaseCode,
				userId,
			});
			console.log(response);
			if (response.data.success) {
				dispatch(appActions.alert('License upgraded', 'success'));
				dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
				success = true;
			} else {
				dispatch(appActions.alert('Purchase code invalid', 'warning'));
			}
		} catch (error) {
			dispatch(appActions.alert('Purchase code invalid', 'warning'));
		} finally {
			if (callbackFn) callbackFn(success);
		}
	};

export const modifyTermLicense =
	({
		capacity,
		userCapacity,
		r2mCapacity,
		expirationDate,
		userExpirationDate,
		r2mExpirationDate,
		purchaseCode,
		userId,
		callbackFn,
	}: {
		capacity: number;
		expirationDate: number;
		userCapacity: number;
		r2mCapacity: number;
		userExpirationDate: number;
		r2mExpirationDate: number;
		purchaseCode: string;
		userId: string;
		callbackFn?: Function;
	}): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());
		purchaseCode = purchaseCode.replace(/-/g, '');

		try {
			const response = await axios.patch(`/api/tenants/${tenantId}/modify-license`, {
				capacity,
				userCapacity,
				r2mCapacity,
				expirationDate,
				userExpirationDate,
				r2mExpirationDate,
				purchaseCode,
				userId,
			});
			console.log(response);
			if (response.data.success) {
				dispatch(appActions.alert('License modified', 'success'));
				dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
			} else {
				dispatch(appActions.alert('Failed to modify license', 'warning'));
			}
		} catch (error) {
			dispatch(appActions.alert('Failed to modify license', 'warning'));
		} finally {
			if (callbackFn) callbackFn();
		}
	};

export const getTermLicense =
	(purchaseCode: string, allowAll: boolean, successFn?: Function, failFn?: Function): AppThunk =>
	async (dispatch, getState) => {
		purchaseCode = purchaseCode.replace(/-/g, '');

		try {
			const response = await axios.get(
				allowAll ? `/api/mp/license/order/${purchaseCode}?all=true` : `/api/mp/license/order/${purchaseCode}`,
			);
			console.log(response);
			// @ts-ignore
			if (response.data.order) {
				// dispatch(appActions.alert('License upgraded', 'success'));
				// dispatch(getSelectedLicenseGroupData());
				// @ts-ignore
				if (successFn) successFn(response.data.order);
			} else {
				dispatch(appActions.alert('Purchase code invalid', 'warning'));
				if (failFn) failFn();
			}
		} catch (error) {
			dispatch(appActions.alert('Purchase code invalid', 'warning'));
			if (failFn) failFn();
		}
	};

export const upgradeLicensePlan =
	({
		orderNumber,
		purchaseCode,
		userId,
		keepPanelUserLoggedIn,
		allowPublicUserRemainLoggedIn,
		deleteCookieDomains,
		emailTo,
		callbackFn,
		capacity,
		userCapacity,
		r2mCapacity,
		expirationDate,
		userExpirationDate,
		r2mExpirationDate,
	}: {
		orderNumber: string;
		purchaseCode: string;
		userId: string;
		keepPanelUserLoggedIn: boolean;
		allowPublicUserRemainLoggedIn: boolean;
		deleteCookieDomains: string[];
		emailTo: 'super-admin' | 'admin' | 'all';
		callbackFn?: Function;
		capacity: number;
		userCapacity: number;
		r2mCapacity: number;
		expirationDate: number;
		userExpirationDate: number;
		r2mExpirationDate: number;
	}): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());
		purchaseCode = purchaseCode.replace(/-/g, '');

		const data = {
			orderNumber,
			purchaseCode,
			userId,
			capacity,
			userCapacity,
			r2mCapacity,
			expirationDate,
			userExpirationDate,
			r2mExpirationDate,
			settings: {
				keepPanelUserLoggedIn,
				allowPublicUserRemainLoggedIn,
				deleteCookieDomains,
				emailTo,
			},
		};

		try {
			const response = await axios.patch(`/api/tenants/${tenantId}/upgrade-plan`, data);
			console.log(response);
			if (response.data.success) {
				dispatch(appActions.alert('License upgraded', 'success'));
				dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
				if (callbackFn) callbackFn();
			} else {
				dispatch(appActions.alert('Failed to upgrade license', 'warning'));
			}
		} catch (error) {
			dispatch(appActions.alert('Failed to upgrade license', 'warning'));
		}
	};

export const getParagonData = (): AppThunk => async (dispatch, getState) => {
	const tenantId = getSelectedLicenseGroupId(getState());

	try {
		const response = await axios.get(`/api/sso/${tenantId}/paragon-data`);
		console.log(response);
		console.log(response.data);
		// @ts-ignore
		if (responseError(response)) {
			dispatch(appActions.alert('settings:get paragon data:fail', 'warning'));
		} else {
			dispatch({
				type: 'GET_PARAGON_DATA_SUCCESS',
				payload: response.data,
			});
			dispatch(getSelectedLicenseGroupData(['license', 'entitlements', 'admin info']));
		}
	} catch (error) {
		dispatch(appActions.alert('settings:get paragon data:fail', 'warning'));
	}
};

export const getTenantGroupInfo = (): AppThunk => async (dispatch, getState) => {
	const tenantId = getSelectedLicenseGroupId(getState());
	const response = await axios.get(`/api/tenants/${tenantId}/group`);
	// @ts-ignore
	if (responseError(response)) {
		dispatch(appActions.alert('tenant groups:details', 'warning'));
	} else {
		const { data: details } = response;
		dispatch({
			type: 'GET_SELECTED_TENANT_GROUP_INFO_SUCCESS',
			payload: {
				tenantId,
				data: details,
			},
		});
	}
};

export const saveMFASettings =
	(require: boolean, grace: number): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		const data = { enabled: require, scheduled: grace > 0, date: 0 };
		if (grace > 0) {
			const d = new Date();
			d.setDate(d.getDate() + grace);
			data.date = d.getTime();
		}

		const response = await axios.patch(`api/mfa/${tenantId}`, data);
		if (response.data.success) {
			dispatch(
				appActions.alert(require ? 'profile:mfa:snackbar' : 'profile:mfa:snackbar unrequired', 'approval'),
			);
		}
	};

export const resetToken = (): AppThunk => async (dispatch, getState) => {
	const response = await axios.post('api/mfa/reset-token');
	console.log(response);
};

export const generateRecoveryCodes =
	(successFn: Function): AppThunk =>
	async (dispatch, getState) => {
		const response = await axios.post('api/mfa/generate-codes');
		successFn(response.data);
	};

export const verifyCode =
	(authCode: string, successFn: Function): AppThunk =>
	async (dispatch, getState) => {
		const response = await axios.post('api/mfa/verify-set-up', { code: authCode });
		successFn(response.data.success);
	};

export const setUpMFA =
	(successFn: Function): AppThunk =>
	async (dispatch, getState) => {
		const response = await axios.post('/api/mfa/set-up');
		successFn(response.data);
	};

export const setNotifications =
	(groupId: string, arr?: string[]): AppThunk =>
	async (dispatch, getState) => {
		try {
			const response = await axios.post(`/api/tenants/${groupId}/notifications`, {
				arr,
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('tenants:notification:fail', 'warning'));
			} else {
				dispatch(getEditedTenantManagerGroups(groupId));
			}
		} catch (error) {
			dispatch(appActions.alert('tenants:notification:fail', 'warning'));
			console.log('error catch', error);
		}
	};

export const setSuspension =
	(tenantId: string, obj?: { [key: string]: boolean }, successFn?: Function): AppThunk =>
	async (dispatch, getState) => {
		if (obj?.terminateTenant) {
			try {
				const response = await axios.post(`/api/tenants/tenant/${tenantId}/terminate`);
				// @ts-ignore
				if (responseError(response)) {
					dispatch(appActions.alert('tenants:suspension:fail', 'warning'));
				} else {
					dispatch(appActions.alert('tenant:manager:delete tenant:success', 'success'));
					if (successFn) {
						successFn(tenantId);
					}
				}
			} catch (error) {
				dispatch(appActions.alert('tenants:suspension:fail', 'warning'));
				console.log('error catch', error);
			}
		} else {
			try {
				const response = await axios.post(`/api/tenants/${tenantId}/suspension`, obj);
				// @ts-ignore
				if (responseError(response)) {
					dispatch(appActions.alert('tenants:suspension:fail', 'warning'));
				} else {
					dispatch(getTenantManagerGroups());
					dispatch(getManagedLicenseGroups());
					dispatch(getTenantById(tenantId));
				}
			} catch (error) {
				dispatch(appActions.alert('tenants:suspension:fail', 'warning'));
				console.log('error catch', error);
			}
		}
	};

export const createTenantManagerGroup =
	(name: string, parentId?: string): AppThunk =>
	async (dispatch, getState) => {
		const groupsById = getTenantManagerGroupsById(getState());
		let pId;
		if (parentId !== undefined) pId = groupsById[parentId];

		try {
			const response = await axios.post('/api/tenants/tenant-manager-groups', {
				name,
				parentId,
				parentName: pId?.name,
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('tenant groups: update fail', 'warning'));
			} else {
				dispatch(getTenantManagerGroups());
			}
		} catch (error) {
			if (error instanceof AxiosError && error.response?.status === 403) {
				dispatch(appActions.alert('tenant groups: create group reject', 'warning'));
			} else {
				dispatch(appActions.alert('tenant groups: update fail', 'warning'));
			}
		}
	};

export const redeemManagerPurchaseCode =
	(purchaseCode: string, userId: string): AppThunk =>
	async (dispatch, getState) => {
		purchaseCode = purchaseCode.replace(/-/g, '');
		try {
			const response = await axios.post('/api/mp/license/redeem', {
				purchaseCode,
				userId,
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('tenant:manager:redeem:failure', 'warning'));
			} else {
				dispatch(getTenantManagerGroups());
				dispatch(appActions.alert('tenant:manager:redeem:success', 'success'));
			}
		} catch (error) {
			dispatch(appActions.alert('tenant:manager:redeem:failure', 'warning'));
		}
	};

const updateDashboardCache = (
	region: 'us-east-1' | 'ca-central-1' | 'eu-central-1' | 'ap-northeast-1',
	aggregateGroups: string[],
) => {
	try {
		if (region === 'ca-central-1') {
			console.log('Dashboard not available on ca-central-1');
			throw Error;
		}
		const logApiUrl = getLogApiUrl(region);
		axios.get(`${logApiUrl}/log/metric/dashboard-agg-cache-refresh?aggtenantId=${aggregateGroups.join(',')}`);
	} catch (e) {
		console.log('Error calling dashboard update', e);
	}
};

export const editTenantManagerGroup =
	(name: string, id: string, parentId?: string, onSuccess?: (value: any) => void): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		try {
			const response = await axios.put('/api/tenants/tenant-manager-groups', {
				name,
				id,
				parentId,
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('tenant groups: update fail', 'warning'));
			} else {
				// const { data } = response;
				// const licenseGroup: TenantGroup = massageRawTenantManagerGroup(data);

				// dispatch({
				// 	type: 'GET_TENANT_GROUP_BY_ID_SUCCESS',
				// 	payload: {
				// 		id,
				// 		data: licenseGroup
				// 	}
				// });
				// const tenantGroups: { [id: string]: TenantGroup } = _.mapValues(
				// 	_.keyBy(response.data, 'id'),
				// 	massageRawTenantManagerGroup
				// );
				// dispatch({
				// 	type: 'GET_TENANT_MANAGER_GROUPS_SUCCESS',
				// 	payload: {
				// 		data: tenantGroups
				// 	}
				// });
				dispatch(getEditedTenantManagerGroups(id));
				if (onSuccess) {
					onSuccess({
						type: 'pushBreadCrumbs',
						payload: {
							name,
							id,
							parent: parentId,
							isGroup: true,
						},
					});
				}
			}
		} catch (error) {
			if (error instanceof AxiosError && error.response?.status === 403) {
				dispatch(appActions.alert('tenant groups: edit group reject', 'warning'));
			} else {
				dispatch(appActions.alert('tenant groups: update fail', 'warning'));
			}
		}
		if (parentId !== undefined) {
			updateDashboardCache(profile.awsRegion, [id, parentId]);
		}
	};

export const editManagedTenantDetails =
	(name: string, id: string, region?: string, parentId?: string, onSuccess?: (value: any) => void): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		try {
			const response = await axios.put(`/api/tenants/edit-tenant-details/${id}`, {
				name,
				region,
				parentId,
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('tenants:details fail', 'warning'));
			} else {
				dispatch(getManagedLicenseGroups());
				// below was for mock data. probably dont need to do this when the API is done.
				// const licenseGroups: { [id: string]: LicenseGroupData } = _.mapValues(
				// 	response.data,
				// 	massageRawLicenseGroup
				// );
				// dispatch({
				// 	type: 'GET_MANAGED_LICENSE_GROUPS_SUCCESS',
				// 	payload: {
				// 		data: licenseGroups
				// 	}
				// });
				if (onSuccess) {
					onSuccess({
						type: 'pushBreadCrumbs',
						payload: {
							name,
							id,
							parent: parentId,
							isGroup: false,
						},
					});
				}
			}
		} catch (error) {
			dispatch(appActions.alert('tenants:details fail', 'warning'));
		}
		if (parentId !== undefined) {
			updateDashboardCache(profile.awsRegion, [id, parentId]);
		}
	};

export const deleteTenantGroup =
	(id: string): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const groupDetails = Object.values(getTenantManagerGroupsById(getState()));
		const childGroups = groupDetails.reduce((prev: string[], curr: TenantGroup) => {
			if (curr?.parentId === id) return [...prev, curr.id];
			return prev;
		}, []);
		console.log(childGroups);
		try {
			const response = await axios.post(`/api/tenants/tenant-group/${id}/delete`);
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('tenant groups: delete fail', 'warning'));
			} else {
				dispatch(getManagedLicenseGroups());
				dispatch(getTenantManagerGroups());
			}
		} catch (error) {
			dispatch(appActions.alert('tenant groups: delete fail', 'warning'));
		}
		updateDashboardCache(profile.awsRegion, childGroups);
	};

export const extendLicense =
	(id: string): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		try {
			const response = await axios.post(`/api/tenants/${id}/extend`);
			// @ts-ignore
			if (responseError(response)) {
				throw new Error('Response error');
			} else {
				dispatch(appActions.alert('tenant:extend:success', 'success'));
				dispatch(getManagedLicenseGroups());
				dispatch(getTenantById(id));
			}
		} catch (error) {
			dispatch(appActions.alert('tenant:extend:fail', 'warning'));
		}
	};

export const getTenantManagerGroupById =
	(groupId: string): AppThunk =>
	async (dispatch, getState) => {
		try {
			const response = await axios.get(`/api/tenants/tenant-manager-group/${groupId}`);
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('tenant groups:details', 'warning'));
			} else {
				const { data: EmailInfo } = response;
				dispatch({
					type: 'GET_GROUP_MANAGER_INFO_BY_ID_SUCCESS',
					payload: {
						groupId,
						data: EmailInfo,
					},
				});
			}
		} catch (error) {
			dispatch(appActions.alert('tenant groups:details', 'warning'));
		}
	};

export const setLoadingTenantGroups = (): AppThunk => async (dispatch, getState) => {
	dispatch({
		type: 'SET_LOADING_MANAGER_GROUPS',
		payload: {
			loading: true,
		},
	});
};

export const saveFormAuthSettings =
	(obj: object): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		try {
			const response = await axios.postForm(`/api/tenants/saml/${tenantId}/client-data`, obj, {
				headers: {
					'Content-Type': 'Content-Type: multipart/form-data',
				},
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('form auth fail', 'warning'));
			} else {
				dispatch(appActions.alert('form auth add', 'success'));
			}
		} catch (error) {
			dispatch(appActions.alert('form auth fail', 'warning'));
		}
	};

export const updateFormAuthSettings =
	(updateObject: object): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		try {
			const response = await axios.patchForm(`/api/tenants/saml/${tenantId}/client-data`, updateObject, {
				headers: {
					'Content-Type': 'Content-Type: multipart/form-data',
				},
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('form auth fail', 'warning'));
			} else {
				dispatch(appActions.alert('form auth edit', 'success'));
			}
		} catch (error) {
			dispatch(appActions.alert('form auth fail', 'warning'));
		}
	};

export const deleteFormAuthProvider =
	(name: string, type: 'sso-user' | 'sso-non-user'): AppThunk =>
	async (dispatch, getState) => {
		const tenantId = getSelectedLicenseGroupId(getState());

		try {
			const response = await axios.post(`/api/tenants/saml/${tenantId}/delete/client-data`, {
				providerName: name,
				type,
			});
			// @ts-ignore
			if (responseError(response)) {
				dispatch(appActions.alert('form auth fail', 'warning'));
			} else {
				dispatch(appActions.alert('form auth delete', 'success'));
			}
		} catch (error) {
			dispatch(appActions.alert('form auth fail', 'warning'));
		}
	};
